// TS mirror of the backend Report Document schema (report_builder/schema.py).
// kind/type are plain strings (open registries) — the renderer maps known ones
// to components and falls back generically for the rest.

export type ClaimType = 'A' | 'B' | 'C' | 'D' | 'E';
export type Tier = 'measured' | 'modeled' | 'directional';

export interface ComputeSpec {
  op: string;
  field: string;
  denominator?: string | null;
  params?: Record<string, unknown>;
}

export interface Provenance {
  surveyIds: number[];
  questionRefs: string[];
  respondentScope: string;
  compute: ComputeSpec;
  respondentIds?: string[] | null;
  baseIds?: string[] | null;
}

export interface ClaimHeader {
  eyebrow?: string;
  title: string;
  value: string;
  unit?: string;
}

export interface Claim {
  id: string;
  type: ClaimType;
  tier: Tier;
  header: ClaimHeader;
  definition?: string | null;
  surveyQuestion?: Record<string, unknown> | null;
  derivation?: string | null;
  method?: string | null;
  breakdown?: Array<Record<string, unknown>> | null;
  demoSections?: Array<Record<string, unknown>> | null;
  verbatim?: Record<string, unknown> | null;
  related?: Array<Record<string, unknown>> | null;
  lede?: string | null;
  commentary?: string[] | null;
  miniStats?: Array<Record<string, unknown>> | null;
  contextualTags?: string[] | null;
  rowColContext?: Record<string, unknown> | null;
  entity?: Record<string, unknown> | null;
  media?: Record<string, unknown> | null;
  filtered?: { activeFilter?: string; fullSampleValue?: string } | null;
  provenance?: Provenance | null;
}

export interface Block {
  type: string;
  data: Record<string, unknown>;
}

export interface Section {
  id: string;
  eyebrow?: string | null;
  title: string;
  kind: string;
  blocks: Block[];
}

export interface NavItem {
  id: string;
  label: string;
}

export interface ReportDocument {
  id: string;
  title: string;
  subtitle?: string | null;
  meta: Record<string, unknown>;
  nav: NavItem[];
  sections: Section[];
  claims: Record<string, Claim>;
}

// A report authored elsewhere and stored verbatim (spec 84); `source` records where it was synced from.
export interface HtmlReportDocument {
  id: string;
  format: 'html';
  title: string;
  html: string;
  source?: { url?: string; version?: string; synced_at?: string } | null;
}

export function isHtmlReport(doc: ReportDocument | HtmlReportDocument): doc is HtmlReportDocument {
  return (doc as HtmlReportDocument).format === 'html';
}
